/* eslint-disable no-unused-vars */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import propTypes from 'prop-types';
import { newAppointment, fetchAppointments } from '../../misc/apiRequests';
import PaymentModal from '../../presentational/properties/payments/paymentModal';
import Swal from 'sweetalert2';
import Exit from '../../../assets/images/exit.svg';
import ArrowLeft from '../../../assets/images/arrow_left.svg';
import ArrowRight from '../../../assets/images/arrow_right.svg';
import '../../../assets/css/appointment.css';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { windowWidth, numberWithCommas } from '../../misc/misc';

const AppointmentTable = ({ property, setShowCalendarModal, handleLoader }) => {
  const { t } = useTranslation();

  const today = useMemo(() => new Date(), []);
  const months = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
  // eslint-disable-next-line camelcase
  const { id, fee, allow_events, max_guests, max_event_guests, max_infants, max_pets } = property;
  const [showModal, setShowModal] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [searchDate, setSearchDate] = useState();
  const [step, setStep] = useState(1);
  const [month, setMonth] = useState(today?.getMonth());
  // const [slots, setSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDateEnd, setSelectedDateEnd] = useState(null);
  // const [htmlDesc, setHtmlDesc] = useState();

  // Step 2 - booking party details
  const [isEvent, setIsEvent] = useState(false);
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);
  const [pets, setPets] = useState(0);

  // eslint-disable-next-line camelcase
  const allowEvents = Boolean(allow_events);
  // A listing with no occupancy set still has to be bookable by at least one person,
  // so fall back to 1 rather than locking the counter at 0.
  // eslint-disable-next-line camelcase
  const guestLimit = Number(max_guests) > 0 ? Number(max_guests) : 1;
  // eslint-disable-next-line camelcase
  const eventGuestLimit = Number(max_event_guests) > 0 ? Number(max_event_guests) : 1;
  // Zero is a meaningful answer for these two - it means none are accepted.
  // eslint-disable-next-line camelcase
  const infantLimit = Number(max_infants) > 0 ? Number(max_infants) : 0;
  // eslint-disable-next-line camelcase
  const petLimit = Number(max_pets) > 0 ? Number(max_pets) : 0;

  const dailyRate = parseFloat(fee) || 0;

  const daysStayed = useMemo(() => {
    if (!selectedDate || !selectedDateEnd) return 0;
    return Math.ceil((new Date(selectedDateEnd) - new Date(selectedDate)) / (1000 * 3600 * 24)) + 1;
  }, [selectedDate, selectedDateEnd]);

  const totalCost = daysStayed * dailyRate;

  // How far into the future appointments can be made;
  const monthLimit = 3;
  const cleaningDays = 1; // Amount of days after a rental is finished being rented for cooldown to clean

  const apptTableRef = useRef(null);

  useEffect(() => {
    if(searchDate) {
      const searchDateArray = searchDate.split('-');
      const newSearchDate = new Date(parseInt(searchDateArray[0]), parseInt(searchDateArray[1]) - 2, parseInt(searchDateArray[2])).toISOString().split('T')[0];
      fetchAppointments(id, newSearchDate)
      .then(response => {
        if (response.success) {
          setAppointments(response.records);
        }
        if (!response.success) {
          // handleModal(response.errors);
          Swal.fire({
            position: 'top-end',
            toast: true,
            timerProgressBar: true,
            icon: 'error',
            title: Array.isArray(response.errors) ? response.errors.join("\n") : response.errors,
            showConfirmButton: false,
            timer: 2000
          })
        }
        handleLoader(false);
      });
    }
  }, [handleLoader, id, searchDate]);

  const handleMakeAppointment = (cancel = false) => {
    if (cancel) return setShowCalendarModal(false);

    handleLoader(true);
    const appointment = {
                          rental_date: selectedDate,
                          rental_date_until: selectedDateEnd,
                          is_event: isEvent,
                          adults,
                          children: isEvent ? 0 : children,
                          infants: isEvent ? 0 : infants,
                          pets: isEvent ? 0 : pets,
                          total_cost: totalCost.toFixed(2)
                        };
    newAppointment(id, appointment)
      .then(response => {
        if (response.success) {
          Swal.fire({
            position: 'top-end',
            toast: true,
            timerProgressBar: true,
            icon: 'success',
            title: response.message,
            showConfirmButton: false,
            timer: 3000
          })
          setShowCalendarModal(false);
        }
        if (!response.success) {
          // handleModal(response.errors);
          Swal.fire({
            position: 'middle',
            icon: 'error',
            title: Array.isArray(response.errors) ? response.errors.join("\n") : response.errors,
            // showConfirmButton: false,
            // timer: 2000
          })
        }
        handleLoader(false);
      });
  };

  const handleMonth = (next = true) => {
    if(next && (month - today?.getMonth() >= monthLimit)) return;
    if(!next && month === today?.getMonth()) return;
    if (!next) setMonth(month - 1);
    if (next) setMonth(month + 1);
  };

  const handleStepReset = () => {
    setStep(1);
    setSelectedDate(null);
    setSelectedDateEnd(null);
  };

  const stepLabels = [t('f_chooseDate'), t('f_guestDetails'), t('f_confirmation')];

  // Used by the mobile carousel arrows and dots. Steps 2 and 3 are only
  // reachable once a date range has been picked.
  const handleStepNav = (target) => {
    if (target < 1 || target > stepLabels.length) return;
    if (target === 1) return handleStepReset();
    if (!selectedDateEnd) return;
    setStep(target);
  };

  // Adults and children share the property's occupancy limit, so each counter's
  // ceiling is whatever the other one hasn't already claimed.
  const adultLimit = isEvent ? eventGuestLimit : Math.max(1, guestLimit - children);
  const childLimit = Math.max(0, guestLimit - adults);

  // The two modes have different ceilings, so trim the running count back
  // into range instead of leaving an over-limit value behind on switch.
  const handleEventToggle = (eventBooking) => {
    setIsEvent(eventBooking);
    setAdults(Math.min(adults, eventBooking ? eventGuestLimit : guestLimit));
  };

  const renderCounter = (label, value, setValue, max, min = 0) => (
    <div className='flex-row justify-flex-between guest-counter'>
      <span className='text-camel'><b>{label}</b></span>
      <div className='flex-row'>
        <button type='button' className='page-btn guest-counter-btn' disabled={value <= min}
                onClick={() => setValue(Math.max(min, value - 1))}>-</button>
        <span className='guest-counter-value'>{value}</span>
        <button type='button' className='page-btn guest-counter-btn' disabled={value >= max}
                onClick={() => setValue(Math.min(max, value + 1))}>+</button>
      </div>
    </div>
  );

  const summaryRow = (label, value) => (
    <div className='flex-row justify-flex-between guest-counter'>
      <span className='text-camel'>{label}</span>
      <span><b>{value}</b></span>
    </div>
  );

  const slot = useCallback((day, active = true) => {
    let isDisabled = false;
    if(month === today.getMonth()) {
      isDisabled = (day + 1) <= today.getDate();
    } else if(month > today.getMonth()){
      isDisabled = false;
    } else if(month < today.getMonth()){
      isDisabled = true;
    }

    if(!active) isDisabled = true;
    const yearLabel = today.getFullYear();
    const monthLabel = (month + 1) >= 10 ? month + 1 : `0${month + 1}`
    const dayLabel = (day + 1) >= 10 ? day + 1 : `0${day + 1}`
    const dataLabel = `${yearLabel}-${monthLabel}-${dayLabel}`;

    // Compare with fetched appointments
    appointments.forEach(x => {
      const endDateArray = x.rental_date_until.split('-');
      // Disable a set number of days (cleaningDays) after rental_date_until
      const cleanerCheck = new Date(parseInt(endDateArray[0]), parseInt(endDateArray[1]) - 1, parseInt(endDateArray[2]) + cleaningDays).toISOString().split('T')[0];
      if ((dataLabel >= x.rental_date && dataLabel <= cleanerCheck)) {
        // console.log(dataLabel, x);
        isDisabled = true;
      }
    })
 
    return isDisabled ? 
      `<td><button disabled class='calendar-slot'>
        ${day + 1}
      </button></td>` : 
      `<td><button class='calendar-slot' data-value=${dataLabel}>
        ${day + 1}
      </button></td>`
  }, [appointments, month, today]);

  // Populate Calendar slots, run once on load
  useEffect(() => {
    if (step !== 1) return

    let htmlDesc = null;

    const handleSlot = (e, day) => {
      e.preventDefault();
      // console.log('handleSlot');
  
      const selectDate = new Date(today.getFullYear(), month, parseInt(day)).toISOString().split('T')[0];
      // console.log(e.target);
      // console.log(selectDate);
      if (!selectedDate) {
        // e.target.classList.toggle("selected-slot");
        setSelectedDate(selectDate);
      } else if (selectedDate) {
        if (selectDate < selectedDate) return setSelectedDate(null);
        // e.target.classList.toggle("selected-slot");
        setSelectedDateEnd(selectDate);
      }
    };

    const populateCalendar = async () => {
      // const daysJsx = [];
      // console.log('populateCalendar');
      const prevDaysInMonth = new Date(today.getFullYear(), month, 0).getDate();
      const daysInMonth = new Date(today.getFullYear(), month+1, 0).getDate();
      const dayStart = new Date(today.getFullYear(), month, 1).getDay();
      const dayEnd = new Date(today.getFullYear(), month+1, 0).getDay();
      let p = dayStart;
      // console.log(today, 'prevDaysInMonth: ', prevDaysInMonth, 'daysInMonth: ', daysInMonth, 'dayStart: ', dayStart)
      let htmlStore = ``;
      let isFirst = true;
      for(let d = 0; d < daysInMonth + dayStart; d++) {
        if (d % 7 === 0) {
          isFirst ? htmlStore += '<tr>' : htmlStore += '</tr><tr>';
          isFirst = false;
        }
        // Start week off with leftover days from previous month
        for(p; p > 0; p--) { htmlStore += slot(prevDaysInMonth - p, false); d += 1; }
  
        // Current days within the month
        htmlStore += slot(d - dayStart);
      }
      // Finish week off with next month dates
      for(let d = 0; d < 6 - dayEnd; d++) {
        htmlStore += slot(d, false);
      }
  
      // console.log(htmlStore);
      // console.log(windowWidth());
      const shorten = windowWidth() < 900;
      const theader = `
        <tr class='calendar-days'>
          <th>${shorten ? t('f_sunday').substring(0,3) : t('f_sunday')}</th>
          <th>${shorten ? t('f_monday').substring(0,3) : t('f_monday')}</th>
          <th>${shorten ? t('f_tuesday').substring(0,3) : t('f_tuesday')}</th>
          <th>${shorten ? t('f_wednesday').substring(0,3) : t('f_wednesday')}</th>
          <th>${shorten ? t('f_thursday').substring(0,3) : t('f_thursday')}</th>
          <th>${shorten ? t('f_friday').substring(0,3) : t('f_friday')}</th>
          <th>${shorten ? t('f_saturday').substring(0,3) : t('f_saturday')}</th>
        </tr>`
  
      htmlDesc = theader + htmlStore;
      // return theader + htmlStore
    };

    setSearchDate(new Date(today.getFullYear(), month, 1).toISOString().split('T')[0]);

    let allCalendorSlots;
    populateCalendar().then(() => {
      // console.log('SetupSlots');
      const bodyDiv = apptTableRef.current;
      if (bodyDiv) bodyDiv.innerHTML = htmlDesc;
      // Add click function to calendar slot buttons
      allCalendorSlots = document.getElementById("Calendar").querySelectorAll('.calendar-slot')
      // const slotsArray = [...allCalendorSlots].map(x => x);
      // setSlots(slotsArray);
      allCalendorSlots.forEach( c => {
        if(c.dataset.value === selectedDate) c.classList.toggle("selected-slot");
        if(c.dataset.value === selectedDateEnd) c.classList.toggle("selected-slot");
        if(parseInt(c.innerText) < parseInt(selectedDateEnd?.split('-')[2]) && parseInt(c.innerText) > parseInt(selectedDate?.split('-')[2])) c.classList.toggle("selected-slot");
        c.addEventListener("click", function(e) { handleSlot(e, c.innerText); });
        // console.log(c.innerText);
      })
    });

    return () => allCalendorSlots.forEach( c => {
      c.addEventListener("click", function(e) { handleSlot(e, c.innerText); });
    })
  }, [month, selectedDate, selectedDateEnd, slot, t, today, step])


  // Handle Confirmation modal
  useEffect(() => {
    const result = async () => {
      const val = await Swal.fire({
        title: 'Confirm Dates',
        icon: 'question',
        // toast: true,
        html: `<div>Are you sure you want to schedule these dates?</div>
          <div>From: <b>${selectedDate}</b></div>
          <div>To: <b>${selectedDateEnd}</b></div>
          <div><b>${daysStayed}</b> day(s) stay</div>
        `,
        showConfirmButton: true,
        showDenyButton: false,
        showCancelButton: true,
      })
      return val.isConfirmed;
    }
    if (selectedDateEnd) {
      result().then(res => {
        if(res) { setStep(2); }
        else {
          // Reset Selected Date values when step 1 is set
          setStep(1);
          setSelectedDate(null);
          setSelectedDateEnd(null);
        }
      })
    }
  }, [selectedDate, selectedDateEnd, daysStayed])

  return (
    <div className="modal">
      <button type="button" className="modal-bg" onClick={() => setShowCalendarModal(false)}/>
      <div className="modal-content" style={{minHeight: 'auto', maxWidth: '1000px', maxHeight: 'none'}}>
        <button type="button" className="modal-exit" onClick={() => setShowCalendarModal(false)}><img src={Exit} alt='exit icon' /></button>
        <div className="container-md">
          <div className='text-center mt-1 mb-2'>
            {/* <h3 className='mb-1'>Steps</h3> */}
            {/* All three steps side by side - tablet and up */}
            <div className='step-tabs flex-row justify-flex-center'>
              <button className={`calendar-step ${step === 1 && 'selected'}`} onClick={() => handleStepReset()}><h4>{stepLabels[0]}</h4></button>
              <button className={`calendar-step ml-1 ${step === 2 && 'selected'}`} onClick={() => selectedDateEnd && setStep(2)}><h4>{stepLabels[1]}</h4></button>
              <button className={`calendar-step ml-1 ${step === 3 && 'selected'}`}><h4>{stepLabels[2]}</h4></button>
            </div>
            {/* One step at a time, paged with arrows - mobile */}
            <div className='step-carousel-wrap'>
              <div className='flex-row justify-flex-center flex-nowrap'>
                <button type='button' className='page-btn' disabled={step === 1}
                        onClick={() => handleStepNav(step - 1)}>
                  <img alt='previous step' src={ArrowLeft}/>
                </button>
                <div className='calendar-step selected step-carousel-label'><h4>{stepLabels[step - 1]}</h4></div>
                <button type='button' className='page-btn' disabled={step === 3 || !selectedDateEnd}
                        onClick={() => handleStepNav(step + 1)}>
                  <img alt='next step' src={ArrowRight}/>
                </button>
              </div>
              <div className='flex-row justify-flex-center mt-1'>
                {stepLabels.map((label, index) => (
                  <button key={label} type='button' aria-label={label}
                          className={`step-dot ${step === index + 1 ? 'active' : ''}`}
                          onClick={() => handleStepNav(index + 1)} />
                ))}
              </div>
            </div>
          </div>
          {/* Calendar Modal */}
          {step === 1 && (
            <div>
              <div className='flex-row justify-flex-center mb-3'>
                <button className='page-btn' onClick={() => handleMonth(false)}><img alt='previous month' src={ArrowLeft}/></button>
                <h3 className='ml-1 mr-1 text-camel'>{months[month]}</h3>
                <button className='page-btn' onClick={() => handleMonth(true)}><img alt='next month' src={ArrowRight}/></button>
              </div>
              <table id='Calendar' className='mx-auto mb-1' ref={apptTableRef}>
              </table>
            </div>
          )}
          {/* Payment Modal */}
          {/* {step === 2 && (
            <PaymentModal
              info={property}
              days={(Math.ceil((new Date(selectedDateEnd) - new Date(selectedDate))/ (1000 * 3600 * 24)) + 1)}
              handleLoader={handleLoader}
              handlePaymentModal={handleMakeAppointment}
              isModal={false}
            />
          )} */}
          {/* Guest / Event Details */}
          {step === 2 && (
            <div className='guest-details mx-auto'>
              <h3 className='mb-1'>{t('f_guestDetails')}</h3>
              {allowEvents && (
                <div className='mb-2'>
                  <div className='mb-1'><b>{t('f_isEventBooking')}</b></div>
                  <div className='flex-row'>
                    <button type='button' className={`calendar-step mr-1 ${isEvent && 'selected'}`}
                            onClick={() => handleEventToggle(true)}><h4>{t('f_yes')}</h4></button>
                    <button type='button' className={`calendar-step ${!isEvent && 'selected'}`}
                            onClick={() => handleEventToggle(false)}><h4>{t('f_no')}</h4></button>
                  </div>
                </div>
              )}
              {isEvent ? (
                <div>
                  <span className='form-sub-text text-grey'>{t('f_maxEventGuests')}: {eventGuestLimit}</span>
                  {renderCounter(t('f_guests'), adults, setAdults, adultLimit, 1)}
                </div>
              ) : (
                <div>
                  <span className='form-sub-text text-grey'>{t('f_maxOccupancy')}: {guestLimit}</span>
                  {renderCounter(t('f_adults'), adults, setAdults, adultLimit, 1)}
                  {renderCounter(t('f_children'), children, setChildren, childLimit)}
                  {renderCounter(t('f_infants'), infants, setInfants, infantLimit)}
                  {renderCounter(t('f_pets'), pets, setPets, petLimit)}
                </div>
              )}
              <div className='flex-row justify-flex-end mt-2'>
                <button className='page-btn mr-2' onClick={() => handleStepReset()}>{t('f_back')}</button>
                <button className='page-btn selected text-white' onClick={() => setStep(3)}>{t('f_next')}</button>
              </div>
            </div>
          )}
          {/* Comfirmation Modal */}
          {step === 3 && (
            <div className='guest-details mx-auto'>
              <h3 className='mb-1'>{t('f_confirmation')}</h3>
              <div className='booking-summary mb-2'>
                {summaryRow(t('f_checkIn'), selectedDate)}
                {summaryRow(t('f_checkOut'), selectedDateEnd)}
                {summaryRow(t('f_lengthOfStay'), `${daysStayed} ${daysStayed === 1 ? t('f_day') : t('f_days')}`)}
                {allowEvents && summaryRow(t('f_isEventBooking'), isEvent ? t('f_yes') : t('f_no'))}
                {isEvent ? summaryRow(t('f_guests'), adults) : (
                  <>
                    {summaryRow(t('f_adults'), adults)}
                    {summaryRow(t('f_children'), children)}
                    {summaryRow(t('f_infants'), infants)}
                    {summaryRow(t('f_pets'), pets)}
                  </>
                )}
                {summaryRow(t('f_ratePerDay'), `$${numberWithCommas(dailyRate.toFixed(2))}`)}
                <div className='flex-row justify-flex-between guest-counter booking-total'>
                  <span className='text-camel'><b>{t('f_totalCost')}</b></span>
                  <span><b>${numberWithCommas(totalCost.toFixed(2))}</b></span>
                </div>
                <span className='form-sub-text text-grey'>{t('f_excludingVat')}</span>
              </div>
              <p>{t('f_apptOwnerNotice')}</p>
              <div className='flex-row justify-flex-end mt-1'>
                <button className='page-btn mr-2' onClick={() => setStep(2)}>{t('f_back')}</button>
                <button className='page-btn selected text-white mr-2' onClick={() => handleMakeAppointment()}>{t('f_confirm')}</button>
                <button className='page-btn' onClick={() => setShowCalendarModal(false)}>{t('f_cancel')}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

AppointmentTable.propTypes = {
  property: propTypes.instanceOf(Object).isRequired,
  handleLoader: propTypes.func.isRequired,
  setShowCalendarModal: propTypes.func.isRequired,
};

export default AppointmentTable;
